import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Paper,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Code as CodeIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';

const PatternPreview = ({ patternBlock, mergedYAML, generatedSVG, generatedPieces = [], onRegenerate }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [svgZoom, setSvgZoom] = useState(1);
  const [selectedPiece, setSelectedPiece] = useState(null);

  const isMultiPiece = generatedPieces && generatedPieces.length > 1;
  const hasPattern = isMultiPiece || (mergedYAML && generatedSVG);

  const handleZoomIn = () => {
    setSvgZoom((prev) => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setSvgZoom((prev) => Math.max(prev - 0.2, 0.5));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Pattern Preview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review the generated pattern before exporting to CLO3D
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRegenerate}
        >
          Regenerate
        </Button>
      </Box>

      {!hasPattern ? (
        <Alert severity="info">
          <Typography variant="body2">
            Pattern not yet generated. Click "Generate Pattern" to create pattern pieces based on your measurements.
          </Typography>
        </Alert>
      ) : (
        <>
          {/* Pattern Info */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip label={`Pattern: ${patternBlock?.name || 'Unknown'}`} color="primary" />
                <Chip label="Status: Generated" color="success" />
                {isMultiPiece && <Chip label={`${generatedPieces.length} pieces`} color="info" />}
                {!isMultiPiece && <Chip label={`Zoom: ${(svgZoom * 100).toFixed(0)}%`} />}
              </Box>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Card>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
              <Tab icon={<VisibilityIcon />} label="Pattern Pieces" iconPosition="start" />
              <Tab icon={<CodeIcon />} label="Merged YAML" iconPosition="start" />
            </Tabs>

            <Box sx={{ p: 2 }}>
              {/* Pattern Pieces Tab */}
              {activeTab === 0 && (
                <Box>
                  {isMultiPiece ? (
                    /* ── Multi-piece grid (jacket etc.) ── */
                    <Box>
                      {selectedPiece !== null && (
                        <Box sx={{ mb: 2 }}>
                          <Button size="small" onClick={() => setSelectedPiece(null)}>
                            ← All pieces
                          </Button>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
                            {generatedPieces[selectedPiece]?.name || generatedPieces[selectedPiece]?.id}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                            <Tooltip title="Zoom Out"><IconButton onClick={handleZoomOut} size="small"><ZoomOutIcon /></IconButton></Tooltip>
                            <Tooltip title="Zoom In"><IconButton onClick={handleZoomIn} size="small"><ZoomInIcon /></IconButton></Tooltip>
                            <Button size="small" onClick={() => setSvgZoom(1)}>Reset</Button>
                          </Box>
                          <Paper variant="outlined" sx={{ bgcolor: 'grey.50', p: 2, overflow: 'auto', maxHeight: 500 }}>
                            <Box sx={{ transform: `scale(${svgZoom})`, transformOrigin: 'top left', transition: 'transform 0.2s' }}>
                              <div dangerouslySetInnerHTML={{ __html: generatedPieces[selectedPiece]?.svg || '' }} />
                            </Box>
                          </Paper>
                        </Box>
                      )}

                      {selectedPiece === null && (
                        <Grid container spacing={2}>
                          {generatedPieces.map((piece, i) => (
                            <Grid item xs={6} sm={4} md={3} key={piece.id || i}>
                              <Card
                                variant="outlined"
                                sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                                onClick={() => { setSelectedPiece(i); setSvgZoom(1); }}
                              >
                                <CardContent sx={{ p: 1.5 }}>
                                  <Paper
                                    variant="outlined"
                                    sx={{ bgcolor: 'white', p: 1, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  >
                                    <div dangerouslySetInnerHTML={{
                                      __html: (piece.svg || '')
                                        .replace(/\swidth="[^"]+"/, ' width="80"')
                                        .replace(/\sheight="[^"]+"/, '')
                                    }} />
                                  </Paper>
                                  <Typography variant="caption" fontWeight={600} display="block" noWrap>
                                    {piece.name || piece.id}
                                  </Typography>
                                  {piece.cutQuantity && (
                                    <Typography variant="caption" color="text.secondary">
                                      Cut × {piece.cutQuantity}{piece.onFold ? ' (on fold)' : ''}
                                    </Typography>
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Box>
                  ) : (
                    /* ── Single SVG ── */
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
                        <Tooltip title="Zoom Out"><IconButton onClick={handleZoomOut} size="small"><ZoomOutIcon /></IconButton></Tooltip>
                        <Tooltip title="Zoom In"><IconButton onClick={handleZoomIn} size="small"><ZoomInIcon /></IconButton></Tooltip>
                        <Button size="small" onClick={() => setSvgZoom(1)}>Reset</Button>
                      </Box>
                      <Paper variant="outlined" sx={{ minHeight: 400, maxHeight: 600, overflow: 'auto', bgcolor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                        <Box sx={{ transform: `scale(${svgZoom})`, transformOrigin: 'center', transition: 'transform 0.2s' }}>
                          <Box sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'white', p: 2 }}>
                            {generatedSVG?.includes('<svg') ? (
                              <div dangerouslySetInnerHTML={{ __html: generatedSVG }} />
                            ) : (
                              <Typography color="text.secondary" sx={{ p: 4 }}>
                                {patternBlock?.name} — generated successfully
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  )}

                  {/* Stats row */}
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined"><CardContent>
                        <Typography variant="caption" color="text.secondary">Pieces</Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {isMultiPiece ? generatedPieces.length : (patternBlock?.pieceCount || 1)}
                        </Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined"><CardContent>
                        <Typography variant="caption" color="text.secondary">Seam Allowance</Typography>
                        <Typography variant="h6" fontWeight={600}>1.0 cm</Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined"><CardContent>
                        <Typography variant="caption" color="text.secondary">Format</Typography>
                        <Typography variant="h6" fontWeight={600}>SVG + DXF</Typography>
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined"><CardContent>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Typography variant="h6" fontWeight={600} color="success.main">Ready</Typography>
                      </CardContent></Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* YAML Preview Tab */}
              {activeTab === 1 && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      This is the merged YAML result from combining all DNA layers. This serves as the specification for pattern generation.
                    </Typography>
                  </Alert>

                  <Paper
                    variant="outlined"
                    sx={{
                      bgcolor: 'grey.900',
                      color: 'grey.100',
                      p: 2,
                      borderRadius: 1,
                      maxHeight: 500,
                      overflow: 'auto',
                    }}
                  >
                    <pre
                      style={{
                        margin: 0,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {mergedYAML}
                    </pre>
                  </Paper>

                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        navigator.clipboard.writeText(mergedYAML);
                        alert('YAML copied to clipboard!');
                      }}
                    >
                      Copy YAML
                    </Button>
                    <Button variant="outlined">
                      Download YAML
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Card>
        </>
      )}
    </Box>
  );
};

export default PatternPreview;
